import type { CSSProperties, KeyboardEvent, ReactNode } from "react";

/**
 * The closed set of visual forms a mermaid fence can dispatch to. Members carry
 * the label painted in the shell's type pill, so a component passes the enum
 * straight through as `type` instead of re-declaring its own string constant.
 */
export enum CmarkdownvisualType {
  Flowchart = "Flowchart",
  Timeline = "Timeline",
  Gantt = "Gantt",
  Pie = "Pie Chart",
  Treemap = "Treemap",
  XyChart = "XY Chart",
  Mindmap = "Mind Map",
  Kanban = "Kanban",
  Sequence = "Sequence",
  Quadrant = "Quadrant Chart",
  Map = "Map",
}

/**
 * Why a recognized visual could not be drawn. `Unparseable` is a parse that
 * returned nothing; `Empty` is a parse that succeeded but described no content.
 * `NoValidLocations` is the map form's single failure: a block naming nothing
 * plottable at all.
 *
 * There is deliberately no reason here for a map whose places have no positions.
 * That block parsed, it named places the reader recognizes, and it renders its
 * location list — sending it to this blanket surface would throw away the only
 * content it has. See `map-render-model.ts`.
 */
export enum CmarkdownvisualErrorReason {
  Unparseable = "unparseable",
  Empty = "empty",
  NoValidLocations = "noValidLocations",
}

export interface CmarkdownvisualErrorProps {
  type: CmarkdownvisualType;
  reason: CmarkdownvisualErrorReason;
}

export interface CmarkdownvisualMetric {
  value: number | string;
  unit: string;
}

export interface CmarkdownvisualProps {
  type: string;
  title?: string | null;
  controls?: ReactNode;
  /**
   * Controls to surface inside the modal view as a centered floating bottom bar.
   * Defaults to undefined -> no bottom bar in modal. Size-changing buttons (collapse,
   * modal-toggle) never appear in the modal regardless of this prop.
   */
  modalControls?: ReactNode;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  containerStyle?: CSSProperties;
  contentStyle?: CSSProperties;
  tabIndex?: number;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  compactPreview?: ReactNode;
  metric?: CmarkdownvisualMetric;
  defaultExpanded?: boolean;
  /**
   * When true, the canvas surface drops its border-radius and inset
   * highlight shadow so it sits flush inside an already-recessed parent
   * (e.g. an inline product-content tile). Modal/compact renditions are
   * unaffected.
   */
  concaved?: boolean;
}
